import React, { useState, useEffect } from "react";
import { User, Check, Lock, RefreshCw, Search } from "react-feather";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { ArrowDown, ArrowUp } from "react-feather";
import CryptoJS from "crypto-js";
import Select from "react-select";
import Swal from "sweetalert2";

const RoomOverview = () => {
  const [filter, setFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState({ value: "HIDE_BLOCKED", label: "Hide Blocked Rooms" });
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const secretKey = process.env.REACT_APP_ENCRYPTION_KEY || "abde100199199111";

  // Define status-based styles and icons
  const statusConfig = {
    "0": { text: "Free", color: "#28a745", icon: <Check size={24} /> },
    "1": { text: "Occupied", color: "#007bff", icon: <User size={24} /> },
    "2": { text: "Dirty", color: "#fd7e14", icon: <RefreshCw size={24} /> },
    "3": { text: "Blocked", color: "#6c757d", icon: <Lock size={24} /> },
  };

  // Scroll function for room type tabs
  const scrollTabs = (direction) => {
    const container = document.querySelector(".room-tabs");
    const scrollAmount = 200;
    container.scrollBy({ left: direction === "left" ? -scrollAmount : scrollAmount, behavior: "smooth" });
  };

  // Mark room as cleaned
  const markRoomCleaned = async (roomNumber) => {
    Swal.fire({
      title: "Want to Mark Room Cleaned?",
      text: `Do you want to mark Room ${roomNumber} as cleaned?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, mark as cleaned!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          Swal.fire({
            title: "Marking Room as Cleaned...",
            text: "Please wait...",
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          const response = await axios.put(
            `${apiUrl}/rooms/mark-clean`,
            { roomNumber, status: "0" },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.status === 200) {
            Swal.fire({
              title: "Room Marked as Cleaned!",
              text: `Room ${roomNumber} has been marked as cleaned.`,
              icon: "success",
            }).then(() => {
              // Update the rooms state to reflect the new status
              setRooms((prevRooms) =>
                prevRooms.map((room) =>
                  room.room_number === roomNumber ? { ...room, room_status: "0" } : room
                )
              );
              // Update the roomTypes state to reflect the new status
          
            });
          } else {
            throw new Error("Failed to update room status");
          }
        } catch (error) {
          console.error("Error marking room as cleaned:", error);
          Swal.fire({
            title: "Error!",
            text: error.response?.data?.message || "An error occurred while marking the room as cleaned.",
            icon: "error",
          });
        }
      }
    });
  };

  // Fetch data from APIs on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch Room Types
        const roomTypeResponse = await axios.get(`${apiUrl}/roomtype/listings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRoomTypes(roomTypeResponse.data.roomTypes);

        // Fetch Room Overview
        const roomResponse = await axios.get(`${apiUrl}/rooms/room-overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRooms(roomResponse.data.rooms);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl, token]);

  // Get unique room types for filter tabs
  const uniqueRoomTypes = ["ALL", ...new Set(roomTypes.map((type) => type.type_name))];

  // Filter rooms based on search input, selected type, and status filter
  const filteredRooms = rooms.filter((room) => {
    const matchesRoomNumber = searchQuery === "" || room.room_number.includes(searchQuery);
    const matchesType = filter === "ALL" || room.room_type === filter;
    let matchesStatus = true;

    switch (statusFilter.value) {
      case "HIDE_BLOCKED":
        matchesStatus = room.room_status !== 3; // Exclude blocked
        break;
      case "SHOW_OCCUPIED":
        matchesStatus = room.room_status === 1; // Only occupied
        break;
      case "UNHIDE_BLOCKED":
        matchesStatus = true; // Show all, including blocked
        break;
      case "DIRTY_ROOMS":
        matchesStatus = room.room_status === 2; // Only dirty
        break;
      case "AVAILABLE_ROOMS":
        matchesStatus = room.room_status === 0; // Only available
        break;
      case "BLOCKED_ROOMS":
        matchesStatus = room.room_status === 3; // Only blocked
        break;
      case "SHOW_ALL":
      default:
        matchesStatus = true; // No status filter
        break;
    }

    return matchesRoomNumber && matchesType && matchesStatus;
  });

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="room-overview-container">
          {/* Header Section */}
          <div className="row align-items-center room-header">
            <div className="col-12 d-flex align-items-center">
              <div style={{ width: "400px" }}>
                <h2 className="room-title">ROOM OVERVIEW</h2>
              </div>
              <div className="d-flex align-items-center flex-grow-1">
                <button className="scroll-arrow" onClick={() => scrollTabs("left")}>
                  <i className="fa fa-chevron-left" />
                </button>
                <div
                  className="room-tabs"
                  style={{
                    overflowX: "hidden",
                    whiteSpace: "nowrap",
                    width: "800px",
                    scrollBehavior: "smooth",
                  }}
                >
                  {uniqueRoomTypes.map((type) => (
                    <button
                      key={type}
                      className={filter === type ? "active" : ""}
                      onClick={() => setFilter(type)}
                      style={{
                        display: "inline-block",
                        margin: "0 5px",
                        padding: "5px 15px",
                        minWidth: "100px",
                        textAlign: "center",
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
                <button className="scroll-arrow" onClick={() => scrollTabs("right")}>
                  <i className="fa fa-chevron-right" />
                </button>
              </div>
            </div>
          </div>

          {/* Room Status Summary, Filter, & Search Box */}
          <div className="row align-items-center room-stats page-header page-top-icons mt-3">
            <div className="col-6 d-flex flex-wrap room-status-summary">
              {loading ? (
                <span>Loading...</span>
              ) : (
                <>
                  <span className="status-all me-2">All {rooms.length}</span>
                  <span className="status-occupied me-2">Occupied {rooms.filter((r) => r.room_status === 1).length}</span>
                  <span className="status-vacant me-2">Available {rooms.filter((r) => r.room_status === 0).length}</span>
                  <span className="status-blocked me-2">Maintenance {rooms.filter((r) => r.room_status === 3).length}</span>
                  <span className="status-dirty">Dirty {rooms.filter((r) => r.room_status === 2).length}</span>
                </>
              )}
            </div>
            <div className="col-2">
              <Select
                value={statusFilter}
                options={[
                  { value: "SHOW_ALL", label: "Show All" },
                  { value: "HIDE_BLOCKED", label: "Hide Blocked Rooms" },
                  { value: "SHOW_OCCUPIED", label: "Show Occupied Rooms" },
                  { value: "AVAILABLE_ROOMS", label: "Available Rooms" },
                  { value: "BLOCKED_ROOMS", label: "Blocked Rooms" },
                  { value: "UNHIDE_BLOCKED", label: "Unhide Blocked Rooms" },
                  { value: "DIRTY_ROOMS", label: "Dirty Rooms" },
                ]}
                onChange={setStatusFilter}
                placeholder="Filter by Status"
              />
            </div>
            <div className="col-4 d-flex justify-content-end align-items-center">
              <div className="search-container" style={{ width: "70%" }}>
                <input
                  type="text"
                  className="search-room"
                  placeholder="Search Room"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Search className="search-icon" size={18} />
              </div>
              <div className="ms-3">
                <Link to="/hospitality/room-overview" className="list-view btn btn-link active mx-2">
                  <i className="fa fa-bars" />
                </Link>
                <Link to="/hospitality/calandar" className="grid-view btn btn-link">
                  <i className="fa fa-calendar-check"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Room Grid */}
          <div className="rooms-grid">
            {loading ? (
              <p>Loading rooms...</p>
            ) : filteredRooms.length > 0 ? (
              filteredRooms.map((room) => (
                <div
                  key={room.room_id}
                  className="room-card"
                  style={{ backgroundColor: statusConfig[room.room_status]?.color || "#ccc" }}
                  data-status={room.room_status}
                >
                  <div className="room-number">{room.room_number}</div>
                  <div className="room-type">{room.room_type}</div>
                  <div className="room-status">
                    {statusConfig[room.room_status]?.icon || <Lock size={24} />}
                  </div>
                  {room.room_status === 1 && room.guest && (
                    <div className="guest-info-hover">
                      <div className="guest-details">
                        <div className="room-status">
                          {statusConfig[room.room_status]?.icon || <Lock size={24} />}
                        </div>
                        <div>
                          {(() => {
                            const encryptedId = CryptoJS.AES.encrypt(
                              room.guest.booking_id?.toString(),
                              secretKey
                            ).toString();
                            return (
                              <Link
                                to={`/hospitality/reservation-details/${encodeURIComponent(encryptedId)}`}
                                className="text-white"
                              >
                                {room.guest.name}
                              </Link>
                            );
                          })()}
                        </div>
                        <div>
                          <ArrowDown size={16} /> {moment(room.guest.check_in_date).format("MMM, DD YYYY")}
                        </div>
                        <div>
                          <ArrowUp size={16} /> {moment(room.guest.check_out_date).format("MMM, DD YYYY")}
                        </div>
                      </div>
                    </div>
                  )}
                  {room.room_status === 2 && (
                    <div className="clean-room-action">
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => markRoomCleaned(room.room_number)}
                        style={{ fontSize: "12px", padding: "5px 10px" }}
                      >
                        Mark as Cleaned
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="no-results">No rooms found.</p>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        .clean-room-action {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          text-align: center;
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default RoomOverview;